import * as XLSX from "xlsx";
import { formatDate, type Board, type Reservation } from "./types";
import { getColumnValue, getColumnNumber, reservationEncaisse, type BoardColumn } from "./boardColumns";

const HEADERS_FOR_TABLE = [
  "NOM", "PRÉNOM", "TÉLÉPHONE", "HEURE", "DATE CRÉATION", "DATE PAIEMENT",
  "AD.", "ENF.", "CVT.", "TARIF AD.", "TARIF ENF.", "MONTANT AD.", "MONTANT ENF.",
  "PRIX TOTAL", "CB", "AMEX", "ESPECE", "CHEQUE", "ZEN", "VIRM",
  "RESTE À PAYER", "TOTAL ENCAISSÉ", "PRISE PAR", "ENCAISSER PAR", "COMMENTAIRE",
  "NUMÉRO DE TABLE", "ACTION",
];

const safeFileTitle = (title?: string) => (title || "tableau").replace(/[/\\?%*:|"<>]/g, "-");

const hexToRgb = (hex?: string): [number, number, number] => {
  const clean = (hex || "#987b55").replace("#", "");
  const r = parseInt(clean.substring(0, 2), 16) || 0;
  const g = parseInt(clean.substring(2, 4), 16) || 0;
  const b = parseInt(clean.substring(4, 6), 16) || 0;
  return [r, g, b];
};

const getContrastRgb = (hex?: string): [number, number, number] => {
  const [r, g, b] = hexToRgb(hex);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? [0, 0, 0] : [255, 255, 255];
};

export function exportExcel(boardTitle: string | undefined, reservations: Reservation[]) {
  if (reservations.length === 0) return;

  const headersForExport = HEADERS_FOR_TABLE.filter((h) => h !== "ACTION");
  const rows = reservations.map((r) => {
    const montantAd = r.adults * r.tarifAdult;
    const montantEnf = r.children * r.tarifChild;
    const prixTotal = montantAd + montantEnf;
    const totalPaiement = r.cb + r.amex + r.cash + r.cheque + r.zen + r.virm;
    const restePayer = prixTotal - totalPaiement;

    return [
      r.lastName, r.firstName, r.phone, r.time, r.createdDate, r.paidDate,
      r.adults, r.children, r.adults + r.children, r.tarifAdult, r.tarifChild,
      montantAd, montantEnf, prixTotal,
      r.cb, r.amex, r.cash, r.cheque, r.zen, r.virm,
      restePayer, r.takenBy, r.cashedBy, r.comment,
    ];
  });

  const data: any[] = [headersForExport, ...rows];

  const excelColLetters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  const totalsRow: any[] = headersForExport.map((_, index) => {
    const rowCount = rows.length;
    const colLetter = excelColLetters[index];
    const numericColumns = [6, 7, 8, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];
    if (index === 0) return "Totaux (filtrés)";
    if (numericColumns.includes(index)) return `=SUM(${colLetter}2:${colLetter}${rowCount + 1})`;
    return "";
  });
  data.push(totalsRow);

  const ws = XLSX.utils.aoa_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Réservations");
  XLSX.writeFile(wb, `Reservations_${safeFileTitle(boardTitle)}.xlsx`);
}

// Colonnes du PDF : NOM/PRÉNOM/TÉLÉPHONE d'abord (pas des colonnes configurables), puis TOUTES les
// colonnes visibles du tableau (Heure, Adultes, Enfants, moyens de paiement, Commentaire, colonnes
// personnalisées...), puis les totaux calculés. Avant ce correctif (2026-09-25, signalé par Emeni sur
// le tableau archivé de Brasserie Polpo), cette liste était figée depuis avant l'existence des colonnes
// configurables : le Commentaire en était explicitement exclu, et aucune colonne personnalisée ni aucun
// moyen de paiement ajouté depuis n'y apparaissait jamais — alors que l'export Excel (excelExport.ts),
// lui, avait déjà été mis à jour pour lire board.columns dynamiquement.
type PdfColumn = { header: string; money: boolean; summed: boolean; value: (r: Reservation) => string | number };

const buildPdfColumns = (board: Board): PdfColumn[] => {
  // Date de réservation / Date de paiement viennent désormais de board.columns (comme Heure, les moyens
  // de paiement, Commentaire...) plutôt que d'être codées en dur ici : elles y figurent par défaut sur
  // tout tableau (voir boardColumns.ts), donc pas besoin de les lister à part.
  const cols: PdfColumn[] = [
    { header: "NOM", money: false, summed: false, value: (r) => r.lastName },
    { header: "PRÉNOM", money: false, summed: false, value: (r) => r.firstName },
    { header: "TÉLÉPHONE", money: false, summed: false, value: (r) => r.phone },
  ];
  board.columns.filter((c) => c.visible && c.id !== "client").forEach((col: BoardColumn) => {
    cols.push({
      header: col.label.toUpperCase(),
      money: col.type === "montant",
      summed: col.type === "montant" || col.type === "nombre",
      value: (r) => (col.type === "montant" || col.type === "nombre"
        ? getColumnNumber(r, col)
        : String(getColumnValue(r, col) ?? "")),
    });
  });
  cols.push({ header: "ENCAISSÉ", money: true, summed: true, value: (r) => reservationEncaisse(r, board.columns) });
  cols.push({ header: "CA THÉORIQUE", money: true, summed: true, value: (r) => r.adults * r.tarifAdult + r.children * r.tarifChild });
  cols.push({
    header: "RESTE DÛ", money: true, summed: true,
    value: (r) => Math.max(0, r.adults * r.tarifAdult + r.children * r.tarifChild - reservationEncaisse(r, board.columns)),
  });
  return cols;
};

export async function exportPdf(board: Board, reservations: Reservation[], brandColor?: string) {
  if (reservations.length === 0) return;

  const { jsPDF } = await import("jspdf");
  const { default: autoTable } = await import("jspdf-autotable");

  const brandFill = hexToRgb(brandColor);
  const brandText = getContrastRgb(brandColor);

  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const title = board.title || "Réservations";
  doc.setFontSize(16);
  doc.setTextColor(brandFill[0], brandFill[1], brandFill[2]);
  const pageWidth = doc.internal.pageSize.getWidth();
  const textWidth = doc.getTextWidth(title);
  doc.text(title, (pageWidth - textWidth) / 2, 40);
  doc.setTextColor(0, 0, 0);

  const columns = buildPdfColumns(board);

  const body = reservations.map((r) => columns.map((col) => {
    const v = col.value(r);
    return typeof v === "number" ? v.toFixed(2) : v;
  }));

  body.push(columns.map((col, i) => {
    if (i === 0) return "TOTAUX";
    if (!col.summed) return "";
    return reservations.reduce((s, r) => s + Number(col.value(r) || 0), 0).toFixed(2);
  }));

  autoTable(doc, {
    head: [columns.map((c) => c.header)],
    body,
    startY: 60,
    styles: { fontSize: 8 },
    headStyles: { fillColor: brandFill, textColor: brandText },
  });

  doc.save(`Reservations_${safeFileTitle(board.title)}.pdf`);
}

export async function exportReservationSheet(boardTitle: string | undefined, boardCreatedAt: string | undefined, reservations: Reservation[]) {
  if (reservations.length === 0) return;

  const { jsPDF } = await import("jspdf");
  const { default: autoTable } = await import("jspdf-autotable");

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const title = "Feuille de réservation";
  doc.setFontSize(12);
  doc.setTextColor(20, 46, 82);
  const pageWidth = doc.internal.pageSize.getWidth();
  const textWidth = doc.getTextWidth(title);
  doc.text(title, (pageWidth - textWidth) / 2, 10);

  const dateStr = boardCreatedAt || new Date().toLocaleDateString("fr-FR");
  const timeNow = new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(`Date: ${dateStr}`, 8, 15);
  doc.text(`Heure d'export: ${timeNow}`, 8, 18);
  doc.text(`Service: ${boardTitle || ""}`, 8, 21);
  doc.setTextColor(0, 0, 0);

  const sortReservations = (arr: Reservation[]) =>
    arr.sort((a, b) => {
      const timeA = a.time || "", timeB = b.time || "";
      if (timeA !== timeB) return timeA.localeCompare(timeB);
      const nameA = `${a.lastName} ${a.firstName}`.toLowerCase();
      const nameB = `${b.lastName} ${b.firstName}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });

  const coversSet = new Set(reservations.map((r) => r.adults + r.children));
  const uniqueCovers = Array.from(coversSet).sort((a, b) => a - b);

  let currentY = 27;
  uniqueCovers.forEach((covers) => {
    const data = sortReservations(reservations.filter((r) => r.adults + r.children === covers));
    if (data.length === 0) return;

    if (currentY > doc.internal.pageSize.getHeight() - 35) {
      doc.addPage();
      currentY = 10;
    }

    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(20, 46, 82);
    doc.text(`Tables de ${covers} couvert${covers > 1 ? "s" : ""}`, 8, currentY);
    currentY += 5;

    const tableData = data.map((r) => [
      `${r.lastName.toUpperCase()} ${r.firstName}`,
      r.time || "",
      covers.toString(),
      r.comment || "",
      r.tableNumber || "",
    ]);

    autoTable(doc, {
      head: [["Nom & Prénom", "Heure", "Couverts", "Commentaire", "N° Table"]],
      body: tableData,
      startY: currentY,
      theme: "grid",
      headStyles: { fillColor: [20, 46, 82], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 10, halign: "center", valign: "middle", cellPadding: 2 },
      bodyStyles: { fontSize: 10, textColor: [0, 0, 0], valign: "middle", cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 42, halign: "left" },
        1: { cellWidth: 15, halign: "center" },
        2: { cellWidth: 14, halign: "center" },
        3: { cellWidth: 81, halign: "left" },
        4: { cellWidth: 16, halign: "center" },
      },
      margin: { left: 8, right: 8 },
    });

    currentY = (doc as any).lastAutoTable.finalY + 4;
  });

  doc.save(`Feuille_de_reservation_${safeFileTitle(boardTitle)}.pdf`);
}
